import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles, ArrowUpCircle, CreditCard } from 'lucide-react';
import DashboardModuleShell from './DashboardModuleShell';

/**
 * Shared "this feature isn't on your plan" page — shown in place of a module's
 * real content when useFeatureAccess() reports hasAccess === false. Generalizes
 * the gate originally built one-off for WhatsApp Flows so every plan-gated
 * module (Voice AI, Workflow Automation, Ads, Enrichment, Bot/Agentic Mode)
 * gets the same upgrade prompt instead of a raw 403 from the API.
 *
 * @param {string} title - module name shown in the page header, e.g. "Voice AI"
 * @param {string} subtitle - one-line module description for the page header
 * @param {Component} icon - lucide icon for the page header
 * @param {object} plan - the company's current plan (from useFeatureAccess), may be null
 * @param {string} featureName - human name of the locked feature, e.g. "Voice AI"
 * @param {string} description - 1-2 sentence pitch for what upgrading unlocks
 * @param {Array<{icon: Component, label: string, desc: string}>} highlights - up to 3 feature highlights
 */
export default function FeatureLockedPage({ title, subtitle, icon, plan, featureName, description, highlights = [] }) {
  const navigate = useNavigate();
  const planName = plan?.name ?? null;

  return (
    <DashboardModuleShell title={title} subtitle={subtitle} icon={icon}>
      <div className="flex flex-col items-center justify-center py-24 bg-white rounded-lg border border-[#D8DDE6] shadow-sm px-8 text-center">
        <div className="w-24 h-24 rounded-lg bg-[#0176D3]/10/5 border border-[#0176D3]/20 flex items-center justify-center mb-8 shadow-[0_2px_8px_rgba(0,0,0,0.16)] shadow-[#0176D3]/10">
          <Lock size={36} className="text-[#0176D3]" />
        </div>

        <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FEF3E1] border border-[#F2C47B] mb-6">
          <Sparkles size={11} className="text-[#B95011]" />
          <span className="text-[10px] font-bold text-[#B95011]">Feature Not Included</span>
        </div>

        <h2 className="text-[22px] font-bold text-[#080707] tracking-tight mb-3">
          {featureName} is not available
        </h2>
        <p className="text-[14px] text-[#706E6B] font-medium leading-relaxed max-w-md mb-3">
          {planName ? (
            <>Your <span className="font-bold text-[#080707]">{planName}</span> plan does not include {featureName}.</>
          ) : (
            <>Your current plan does not include {featureName}.</>
          )}
          {description ? ` ${description}` : ''}
        </p>

        {highlights.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full max-w-lg mb-10 mt-4">
            {highlights.map(({ icon: Icon, label, desc }) => (
              <div key={label} className="p-4 rounded-lg bg-[#F4F6F9] border border-[#D8DDE6] text-left">
                <div className="w-8 h-8 rounded-lg bg-white border border-[#D8DDE6] flex items-center justify-center mb-3 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                  <Icon size={14} className="text-[#0176D3]" />
                </div>
                <p className="text-[11px] font-bold text-[#080707] mb-0.5">{label}</p>
                <p className="text-[10px] text-[#706E6B] font-medium leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-center gap-3">
          <button
            onClick={() => navigate('/dashboard/subscription')}
            className="flex items-center gap-2 px-8 py-3.5 bg-[#0176D3] text-white text-[13px] font-bold rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] shadow-[#0176D3]/25 hover:bg-[#0165b8] transition-all whitespace-nowrap"
          >
            <ArrowUpCircle size={16} /> Upgrade Plan
          </button>
          <button
            onClick={() => navigate('/dashboard/subscription')}
            className="flex items-center gap-2 px-6 py-3.5 bg-white border border-[#D8DDE6] text-[#706E6B] text-[13px] font-bold rounded-lg hover:border-[#0176D3] hover:text-[#0176D3] transition-all whitespace-nowrap"
          >
            <CreditCard size={15} /> View All Plans
          </button>
        </div>
      </div>
    </DashboardModuleShell>
  );
}
